/** @jsxImportSource @emotion/react */
import { css, type CSSObject } from '@emotion/react';
import type { CSSProperties } from 'react';

import { styleguideColors, styleguideFontSize, styleguideFontWeight } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import {
  styleguideControlSizes,
  styleguideFontFamily,
  styleguideLetterSpacing,
  styleguideRadius,
  styleguideSpacing,
  withOpacity,
} from '@kaiizen/kai-shiki/core';

const COLORS = styleguideColors;
const CONTROL_SIZES = styleguideControlSizes;
const FONT_FAMILY = styleguideFontFamily;
const FONT_SIZE = styleguideFontSize;
const FONT_WEIGHT = styleguideFontWeight;
const LETTER_SPACING = styleguideLetterSpacing;
const RADIUS = styleguideRadius;
const SPACING = styleguideSpacing;

const ACTION_BUTTON_BASE_STYLE: CSSProperties = {
  minHeight: 36,
  borderRadius: RADIUS.sharp,
  fontFamily: FONT_FAMILY.mono,
  fontSize: pxToRem(FONT_SIZE.monoLabel),
  fontWeight: FONT_WEIGHT.bold,
  letterSpacing: LETTER_SPACING.monoLabel,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  padding: `${CONTROL_SIZES.buttonPaddingY}px ${CONTROL_SIZES.buttonPaddingX}px`,
};

export const PRIMARY_ACTION_BUTTON_STYLE: CSSProperties = {
  ...ACTION_BUTTON_BASE_STYLE,
  border: `1px solid ${COLORS.ink}`,
  background: COLORS.ink,
  color: COLORS.paper,
};

export const SECONDARY_ACTION_BUTTON_STYLE: CSSProperties = {
  ...ACTION_BUTTON_BASE_STYLE,
  border: `1px solid ${COLORS.stroke}`,
  background: COLORS.white,
  color: COLORS.ink,
};

export const SECTION_STYLE = css`
  label: ARTIFACT_PANEL_SECTION;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.s4}px;
  margin-bottom: ${SPACING.s8}px;
`;

export const SECTION_HEADER_ROW_STYLE = css`
  label: ARTIFACT_PANEL_SECTION_HEADER_ROW;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: ${SPACING.s4}px;
  flex-wrap: wrap;
`;

export const SECTION_COPY_STYLE = css`
  label: ARTIFACT_PANEL_SECTION_COPY;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.s2}px;
  max-width: 780px;
`;

export const EYEBROW_STYLE = css`
  label: ARTIFACT_PANEL_EYEBROW;
  color: ${COLORS.textTertiary};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoMicro)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
`;

export const SECTION_TITLE_STYLE = css`
  label: ARTIFACT_PANEL_SECTION_TITLE;
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.display};
  font-size: ${pxToRem(FONT_SIZE.displayMedium)};
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 1.05;
`;

export const SECTION_DESCRIPTION_STYLE = css`
  label: ARTIFACT_PANEL_SECTION_DESCRIPTION;
  color: ${COLORS.textSecondary};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodyDefault)};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.55;
`;

export const THREE_UP_CARD_GRID_STYLE = css`
  label: ARTIFACT_PANEL_THREE_UP_CARD_GRID;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${SPACING.s4}px;

  @media (max-width: 1180px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const selectableCardBaseStyle = css`
  label: ARTIFACT_PANEL_SELECTABLE_CARD_BASE;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.s3}px;
  padding: ${SPACING.s5}px;
  border: 1px solid ${COLORS.stroke} !important;
  background: ${COLORS.white};
  box-shadow: none;
  cursor: pointer;
  transition:
    background-color 120ms ease,
    border-color 120ms ease,
    box-shadow 120ms ease,
    transform 120ms ease;

  &:hover {
    border: 1px solid ${COLORS.ink} !important;
    background: ${COLORS.white};
    box-shadow: 0 ${SPACING.s1}px ${SPACING.s3}px ${withOpacity(COLORS.ink, 0.06)};
    transform: translateY(-1px);
  }

  &:focus-visible {
    border: 1px solid ${COLORS.ink} !important;
    outline: 2px solid ${COLORS.ink};
    outline-offset: 2px;
    transform: translateY(-1px);
  }
`;

export const HISTORY_CARD_STYLE = css`
  label: ARTIFACT_PANEL_HISTORY_CARD;
  min-height: 180px;
`;

export const COPY_CARD_STYLE = css`
  label: ARTIFACT_PANEL_COPY_CARD;
  min-height: 236px;
`;

export const EMPTY_PANEL_STATE_STYLE = css`
  label: ARTIFACT_PANEL_EMPTY_PANEL_STATE;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 196px;
  padding: ${SPACING.s7}px ${SPACING.s5}px;
  border-style: dashed;
  background: ${COLORS.white};
`;

export const EMPTY_PANEL_CONTENT_STYLE = css`
  label: ARTIFACT_PANEL_EMPTY_PANEL_CONTENT;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING.s2}px;
  text-align: center;
`;

export const EMPTY_PANEL_TITLE_STYLE = css`
  label: ARTIFACT_PANEL_EMPTY_PANEL_TITLE;
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.display};
  font-size: ${pxToRem(FONT_SIZE.displaySmall)};
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 1.15;
`;

export const EMPTY_PANEL_DESCRIPTION_STYLE = css`
  label: ARTIFACT_PANEL_EMPTY_PANEL_DESCRIPTION;
  max-width: 34ch;
  color: ${COLORS.textSecondary};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodyDefault)};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.6;
`;

export const EMPTY_CANVAS_STATE_STYLE = css`
  label: ARTIFACT_PANEL_EMPTY_CANVAS_STATE;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: min(420px, calc(100vh - 220px));
  padding: ${SPACING.s8}px ${SPACING.s5}px;
`;

export const EMPTY_CANVAS_CONTENT_STYLE = css`
  label: ARTIFACT_PANEL_EMPTY_CANVAS_CONTENT;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING.s4}px;
  max-width: 42ch;
  text-align: center;
`;

export const EMPTY_CANVAS_ICON_BOX_STYLE = css`
  label: ARTIFACT_PANEL_EMPTY_CANVAS_ICON_BOX;
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  border: 1px solid ${COLORS.stroke};
  background: ${COLORS.surfaceMuted};
  color: ${COLORS.textSecondary};
`;

export const EMPTY_CANVAS_TITLE_STYLE = css`
  label: ARTIFACT_PANEL_EMPTY_CANVAS_TITLE;
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.display};
  font-size: ${pxToRem(FONT_SIZE.displayMedium)};
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 1.05;
`;

export const EMPTY_CANVAS_DESCRIPTION_STYLE = css`
  label: ARTIFACT_PANEL_EMPTY_CANVAS_DESCRIPTION;
  color: ${COLORS.textSecondary};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodyDefault)};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.65;
`;

export const EMPTY_CANVAS_DETAIL_STYLE = css`
  label: ARTIFACT_PANEL_EMPTY_CANVAS_DETAIL;
  color: ${COLORS.textTertiary};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
  margin-top: ${SPACING.s3}px;
`;

export const CARD_KICKER_STYLE = css`
  label: ARTIFACT_PANEL_CARD_KICKER;
  color: ${COLORS.textTertiary};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoMicro)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
`;

export const CARD_TITLE_STYLE = css`
  label: ARTIFACT_PANEL_CARD_TITLE;
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.display};
  font-size: ${pxToRem(FONT_SIZE.displaySmall)};
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 1.2;
`;

export const CARD_SUBTITLE_STYLE = css`
  label: ARTIFACT_PANEL_CARD_SUBTITLE;
  color: ${COLORS.textSecondary};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.55;
`;

export const CARD_DIVIDER_STYLE = css`
  label: ARTIFACT_PANEL_CARD_DIVIDER;
  border-top: 1px solid ${COLORS.stroke};
`;

export const CARD_AMOUNT_ROW_STYLE = css`
  label: ARTIFACT_PANEL_CARD_AMOUNT_ROW;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${SPACING.s3}px;
  margin-top: auto;
`;

export const CARD_AMOUNT_STYLE = css`
  label: ARTIFACT_PANEL_CARD_AMOUNT;
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodyLarge)};
  font-weight: ${FONT_WEIGHT.bold};
`;

export const BADGE_ROW_STYLE = css`
  label: ARTIFACT_PANEL_BADGE_ROW;
  display: flex;
  gap: ${SPACING.s2}px;
  flex-wrap: wrap;
`;

export const badgeStyle = (tone: 'accent' | 'neutral' | 'danger') =>
  css`
  label: ARTIFACT_PANEL_BADGE;
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 ${SPACING.s3}px;
  border: 1px solid ${
    tone === 'danger'
      ? withOpacity(COLORS.error, 0.36)
      : tone === 'accent'
      ? COLORS.ink
      : COLORS.stroke
  };
  border-radius: ${RADIUS.sharp}px;
  background: ${
    tone === 'danger'
      ? withOpacity(COLORS.error, 0.08)
      : tone === 'accent'
      ? withOpacity(COLORS.accent, 0.6)
      : COLORS.surfaceMuted
  };
  color: ${tone === 'danger' ? COLORS.error : COLORS.ink};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoLabel};
  text-transform: uppercase;
  white-space: nowrap;
`;

export const COPY_CARD_ACTIONS_STYLE = css`
  label: ARTIFACT_PANEL_COPY_CARD_ACTIONS;
  display: flex;
  justify-content: flex-end;
  gap: ${SPACING.s3}px;
  margin-top: auto;
  flex-wrap: wrap;
`;

export const LOADING_TEXT_STYLE = css`
  label: ARTIFACT_PANEL_LOADING_TEXT;
  color: ${COLORS.textSecondary};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodyDefault)};
  font-weight: ${FONT_WEIGHT.regular};
`;

export const CREATE_MODAL_CARD_STYLE = css`
  label: ARTIFACT_PANEL_CREATE_MODAL_CARD;
  width: min(100%, 560px);
  display: flex;
  flex-direction: column;
  gap: ${SPACING.s4}px;
  padding: ${SPACING.s6}px;
  border-color: ${COLORS.ink};
  box-shadow: 0 24px 60px ${withOpacity(COLORS.ink, 0.16)};
  background: ${COLORS.white};
`;

export const MODAL_TITLE_STYLE = css`
  label: ARTIFACT_PANEL_MODAL_TITLE;
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.display};
  font-size: ${pxToRem(FONT_SIZE.displayModalTitle)};
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 1.15;
`;

export const MODAL_FORM_STYLE = css`
  label: ARTIFACT_PANEL_MODAL_FORM;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.s4}px;
`;

export const FIELD_GROUP_STYLE = css`
  label: ARTIFACT_PANEL_FIELD_GROUP;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.s2}px;
`;

export const FIELD_SHELL_STYLE = {
  position: 'relative',
  minHeight: 56,
  width: '100%',
  padding: '20px 16px 14px',
  border: `1px solid ${COLORS.stroke}`,
  borderRadius: RADIUS.sharp,
  background: COLORS.white,
  display: 'flex',
  alignItems: 'center',
} satisfies CSSObject;

export const FIELD_LABEL_STYLE = {
  position: 'absolute',
  top: -7,
  left: 12,
  zIndex: 1,
  padding: '0 4px',
  background: COLORS.white,
  color: COLORS.textTertiary,
  fontFamily: FONT_FAMILY.mono,
  fontSize: pxToRem(FONT_SIZE.monoCaption),
  fontWeight: FONT_WEIGHT.bold,
  letterSpacing: LETTER_SPACING.monoCaption,
  textTransform: 'uppercase',
} satisfies CSSObject;

export const FIELD_INPUT_STYLE = {
  width: '100%',
  border: 0,
  padding: 0,
  background: 'transparent',
  outline: 'none',
  color: COLORS.ink,
  fontFamily: FONT_FAMILY.body,
  fontSize: pxToRem(FONT_SIZE.bodyDefault),
  fontWeight: FONT_WEIGHT.regular,
  lineHeight: 1.4,
  '&::placeholder': {
    color: COLORS.textTertiary,
  },
} satisfies CSSObject;

export const FIELD_SELECT_STYLE = css`
  label: ARTIFACT_PANEL_FIELD_SELECT;
  width: 100%;
  border: 0;
  padding: 0;
  padding-right: 28px;
  appearance: none;
  background: transparent;
  outline: none;
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodyDefault)};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.4;
  cursor: pointer;
`;

export const FIELD_SELECT_ICON_STYLE = css`
  label: ARTIFACT_PANEL_FIELD_SELECT_ICON;
  position: absolute;
  right: ${SPACING.s4}px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: ${COLORS.textSecondary};
`;

export const FIELD_HELPER_STYLE = css`
  label: ARTIFACT_PANEL_FIELD_HELPER;
  color: ${COLORS.textSecondary};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.5;
`;

export const MODAL_SETTING_ROW_STYLE = css`
  label: ARTIFACT_PANEL_MODAL_SETTING_ROW;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.s4}px;
  padding: ${SPACING.s4}px;
  border: 1px solid ${COLORS.stroke};
  background: ${COLORS.surfaceMuted};
`;

export const MODAL_SETTING_COPY_STYLE = css`
  label: ARTIFACT_PANEL_MODAL_SETTING_COPY;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.s1}px;
`;

export const MODAL_SETTING_TITLE_STYLE = css`
  label: ARTIFACT_PANEL_MODAL_SETTING_TITLE;
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoLabel)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoLabel};
  text-transform: uppercase;
`;

export const MODAL_SETTING_BODY_STYLE = css`
  label: ARTIFACT_PANEL_MODAL_SETTING_BODY;
  color: ${COLORS.textSecondary};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.5;
`;

export const MODAL_ACTIONS_STYLE = css`
  label: ARTIFACT_PANEL_MODAL_ACTIONS;
  display: flex;
  justify-content: flex-end;
  gap: ${SPACING.s3}px;
  flex-wrap: wrap;
  margin-top: ${SPACING.s2}px;
`;
